import React, { useState, useEffect, useRef, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import ProfileCard from '../components/ProfileCard';
import '../Dashboard.css';
import '../App.css';
import './Agent.css';
import API from '../services/api';  
import Modals from '../components/Models';
import { useNavigate, useLocation } from 'react-router-dom';

const Agent = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
  const [activeTab, setActiveTab] = useState('agent');
  const [message, setMessage] = useState(''); 
  const [chat, setChat] = useState([]);
  const [sessions, setSessions] = useState([]); // { id, title, messages }
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [showSessionsPanel, setShowSessionsPanel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileImage, setProfileImage] = useState("");
  const token = localStorage.getItem('token');
  // derive per-user storage keys so sessions don't leak between users
  // Prefer stable identifiers (user id or email). Only fall back to
  // token when user info is not available to avoid losing sessions when
  // tokens rotate on login/logout.
  const _rawUser = localStorage.getItem('user');
  let _storageSuffix = '';
  try {
    const _u = _rawUser ? JSON.parse(_rawUser) : null;
    if (_u && (_u.id || _u.user_id)) {
      _storageSuffix = `_${_u.id || _u.user_id}`;
    } else if (_u && _u.email) {
      _storageSuffix = `_${String(_u.email).replace(/[^a-z0-9]/gi,'')}`;
    } else if (token) {
      _storageSuffix = `_${token.slice(0,8)}`;
    }
  } catch (e) {
    try {
      const _u = _rawUser ? JSON.parse(_rawUser) : null;
      if (_u && (_u.id || _u.user_id)) _storageSuffix = `_${_u.id || _u.user_id}`;
      else if (_u && _u.email) _storageSuffix = `_${String(_u.email).replace(/[^a-z0-9]/gi,'')}`;
      else if (token) _storageSuffix = `_${token.slice(0,8)}`;
    } catch (e2) {
      if (token) _storageSuffix = `_${token.slice(0,8)}`;
    }
  }

  // If no `user` object was stored, try older single-key fields that some
  // clients set (`userEmail`, `username`) to derive a stable suffix.
  if (!_storageSuffix) {
    try {
      const ue = localStorage.getItem('userEmail');
      const un = localStorage.getItem('username');
      if (ue) _storageSuffix = `_${String(ue).replace(/[^a-z0-9]/gi,'')}`;
      else if (un) _storageSuffix = `_${String(un).replace(/[^a-z0-9]/gi,'')}`;
    } catch (e) {
      // ignore
    }
  }

  // If we still don't have a suffix, fall back to token fragment.
  if (!_storageSuffix && token) {
    _storageSuffix = `_${token.slice(0,8)}`;
  }

  // Migrate from a token-derived key to the stable per-user key when
  // possible: if our stable key is absent but a token-based key exists
  // (common after previous builds used token suffix), copy it over so the
  // user's sessions persist across token rotations.
  try {
    const SESSIONS_KEY = `agent_sessions${_storageSuffix}`;
    const ACTIVE_KEY = `agent_active_session${_storageSuffix}`;
    // token-based key used previously
    if (token) {
      const tokenKey = `agent_sessions_${token.slice(0,8)}`;
      const tokenActiveKey = `agent_active_session_${token.slice(0,8)}`;
      if (!localStorage.getItem(SESSIONS_KEY) && localStorage.getItem(tokenKey)) {
        const val = localStorage.getItem(tokenKey);
        localStorage.setItem(SESSIONS_KEY, val);
        const a = localStorage.getItem(tokenActiveKey);
        if (a) localStorage.setItem(ACTIVE_KEY, a);
        // remove the old token-based keys to avoid confusion
        try { localStorage.removeItem(tokenKey); localStorage.removeItem(tokenActiveKey); } catch (e) {}
      }
    }
  } catch (e) {
    // ignore storage errors
  }
  const SESSIONS_KEY = `agent_sessions${_storageSuffix}`;
  const ACTIVE_KEY = `agent_active_session${_storageSuffix}`;

  // Fetch profile image from backend (like Dashboard)
  const fetchProfileImage = useCallback(async () => {
    try {
      const res = await API.get("/auth/profile/image", { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.profile_image) {
        setProfileImage(res.data.profile_image);
      }
    } catch (err) {
      // ignore
    }
  }, [token]);

  const [isDeleteChatConfirmOpen, setIsDeleteChatConfirmOpen] = useState(false);
  const [isDeletingChat, setIsDeletingChat] = useState(false);
  useEffect(() => {
    fetchProfileImage();
  }, [fetchProfileImage]);
  const textareaRef = useRef(null);
  const messagesRef = useRef(null);
  const sessionsPanelRef = useRef(null);
  const sessionsToggleRef = useRef(null);
  const prevActiveSessionRef = useRef(null);
  const [showScrollToLatest, setShowScrollToLatest] = useState(false);
  const isAtBottomRef = useRef(true);
  const didInitialScrollRef = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();

  const [showHello, setShowHello] = useState(false);

  // Show the CommandX animation once per login (per-user token).
  // If the user hasn't seen it for this login we show it on first visit
  // to the Agent page. If the sidebar sets `commandxTrigger` we also
  // consume it so it doesn't retrigger repeatedly.
  useEffect(() => {
    try {
      // Only show CommandX when the page was reloaded (not when the user
      // clicks the CommandX header). We detect reloads via the Navigation
      // Timing API and show the animation once per page load using a
      // sessionStorage flag. We still clear any sidebar trigger so clicks
      // won't cause the animation.
      const shownThisLoad = sessionStorage.getItem('commandxShownThisLoad') === 'true';

      // Guard: Navigation Timing API support
      let isReload = false;
      try {
        const navEntries = performance.getEntriesByType && performance.getEntriesByType('navigation');
        const nav = Array.isArray(navEntries) && navEntries[0] ? navEntries[0] : null;
        if (nav && nav.type === 'reload') isReload = true;
      } catch (e) {
        // ignore
      }

      if (isReload && !shownThisLoad) {
        sessionStorage.setItem('commandxShownThisLoad', 'true');
        setShowHello(true);
        setTimeout(() => setShowHello(false), 1800);
        return;
      }

      // Ensure any click-trigger is consumed so clicking doesn't replay it
      try { sessionStorage.removeItem('commandxTrigger'); } catch (e) {}
    } catch (e) {
      // ignore storage errors
    }
  }, []);

  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  useEffect(() => {
    if (!token) navigate('/login');
  }, [token, navigate]);

  useEffect(() => {
    const handleResize = () => setIsSidebarOpen(window.innerWidth > 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // keep activeTab synced from URL if needed
    const parts = location.pathname.split('/').filter(Boolean);
    if (parts[0] === 'agent') setActiveTab('agent');
  }, [location.pathname]);
  // Load sessions from localStorage if present, otherwise fetch single-session history
  useEffect(() => {
    const load = async () => {
      try {
        // prefer per-user key; fall back to legacy global key and migrate
        const raw = localStorage.getItem(SESSIONS_KEY);
        const rawActive = localStorage.getItem(ACTIVE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          setSessions(parsed);
          if (rawActive) setActiveSessionId(rawActive);
          const active = parsed.find(s => s.id === rawActive) || parsed[0];
          setChat(active ? (active.messages || []) : []);
          return;
        }
        // If a legacy global `agent_sessions` key exists, remove it to
        // prevent cross-account leakage. Do NOT migrate global data into
        // per-user storage automatically.
        try {
          const legacyRaw = localStorage.getItem('agent_sessions');
          if (legacyRaw) {
            localStorage.removeItem('agent_sessions');
            localStorage.removeItem('agent_active_session');
            // don't return here; fallthrough to backend fetch / new session
          }
        } catch (e) {
          // ignore storage errors
        }
      } catch (e) {
        // ignore parse errors
      }

      // Recompute storage suffix at load time in case `user` info was
      // written after initial render (login timing). This ensures we
      // migrate token-based keys into the stable per-user key when
      // possible.
      let runtimeSuffix = '';
      try {
        const rawUser = localStorage.getItem('user');
        const t = localStorage.getItem('token');
        const u = rawUser ? JSON.parse(rawUser) : null;
        if (u && (u.id || u.user_id)) runtimeSuffix = `_${u.id || u.user_id}`;
        else if (u && u.email) runtimeSuffix = `_${String(u.email).replace(/[^a-z0-9]/gi,'')}`;
        else {
          const ue = localStorage.getItem('userEmail');
          const un = localStorage.getItem('username');
          if (ue) runtimeSuffix = `_${String(ue).replace(/[^a-z0-9]/gi,'')}`;
          else if (un) runtimeSuffix = `_${String(un).replace(/[^a-z0-9]/gi,'')}`;
          else if (t) runtimeSuffix = `_${t.slice(0,8)}`;
        }
      } catch (e) {
        // fall back to top-level computed suffix
        runtimeSuffix = _storageSuffix;
      }

      const runtimeSessionsKey = `agent_sessions${runtimeSuffix}`;
      const runtimeActiveKey = `agent_active_session${runtimeSuffix}`;

      try {
        // If a token-derived key exists but our stable key is empty,
        // migrate it here. Token fragment may differ from current
        // stable suffix if token rotated earlier.
        const t = localStorage.getItem('token');
        if (t) {
          const tokenKey = `agent_sessions_${t.slice(0,8)}`;
          const tokenActiveKey = `agent_active_session_${t.slice(0,8)}`;
          if (!localStorage.getItem(runtimeSessionsKey) && localStorage.getItem(tokenKey)) {
            const val = localStorage.getItem(tokenKey);
            localStorage.setItem(runtimeSessionsKey, val);
            const a = localStorage.getItem(tokenActiveKey);
            if (a) localStorage.setItem(runtimeActiveKey, a);
            try { localStorage.removeItem(tokenKey); localStorage.removeItem(tokenActiveKey); } catch (e) {}
          }
        }
      } catch (e) {
        // ignore storage errors
      }

      try {
        const raw = localStorage.getItem(runtimeSessionsKey);
        const rawActive = localStorage.getItem(runtimeActiveKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          // If the stored sessions are only the single-placeholder
          // `s_single`, prefer fetching backend history that contains
          // real `session_id` values instead of using stale local data.
          const isSinglePlaceholder = Array.isArray(parsed) && parsed.length === 1 && parsed[0] && String(parsed[0].id).startsWith('s_single');
          if (!isSinglePlaceholder) {
            setSessions(parsed);
            if (rawActive) setActiveSessionId(rawActive);
            const active = parsed.find(s => s.id === rawActive) || parsed[0];
            setChat(active ? (active.messages || []) : []);
            return;
          }
          // else fallthrough to fetch backend history and replace local
        }
      } catch (e) {
        // ignore parse errors
      }

      // fallback: fetch from backend into a single session
      try {
        const res = await API.get('/api/agent/history', { headers: { Authorization: `Bearer ${token}` } });
        const apiMsgs = (res.data && Array.isArray(res.data.history)) ? res.data.history : [];
        // Group backend history by session_id when available
        const grouped = {};
        apiMsgs.forEach(m => {
          const sid = m.session_id || `s_single`;
          if (!grouped[sid]) grouped[sid] = [];
          grouped[sid].push({ role: m.role, text: m.message });
        });
        const sessionsFromBackend = Object.keys(grouped).map((sid, idx) => ({ id: sid.startsWith('s_') ? sid : `s_backend_${idx}_${Date.now()}`, title: '', messages: grouped[sid] }));
        const initial = sessionsFromBackend.length ? sessionsFromBackend[0] : { id: `s_${Date.now()}`, title: '', messages: [] };
        setSessions(sessionsFromBackend.length ? sessionsFromBackend : [initial]);
        setActiveSessionId(initial.id);
        setChat(initial.messages);
        try { localStorage.setItem(runtimeSessionsKey, JSON.stringify(sessionsFromBackend.length ? sessionsFromBackend : [initial])); localStorage.setItem(runtimeActiveKey, initial.id); } catch (e) {}
      } catch (err) {
        const initial = { id: `s_${Date.now()}`, title: '', messages: [] };
        setSessions([initial]);
        setActiveSessionId(initial.id);
        setChat([]);
        try { localStorage.setItem(runtimeSessionsKey, JSON.stringify([initial])); localStorage.setItem(runtimeActiveKey, initial.id); } catch (e) {}
      }
    };
    load();
  }, [token, SESSIONS_KEY, ACTIVE_KEY, _storageSuffix]);

  // Close sessions panel when clicking outside of it (click-away).
  useEffect(() => {
    if (!showSessionsPanel) return;
    const handleClickOutside = (e) => {
      try {
        const target = e.target;
        if (sessionsPanelRef.current && sessionsPanelRef.current.contains(target)) return;
        if (sessionsToggleRef.current && sessionsToggleRef.current.contains(target)) return;
        setShowSessionsPanel(false);
      } catch (err) {
        // ignore
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSessionsPanel]);

  const sendMessage = async () => {
    if (!message.trim()) return;
    const userMsg = { role: 'user', text: message };
    setChat((c) => {
      const next = [...c, userMsg];
      if (activeSessionId) setSessions(prev => prev.map(s => s.id === activeSessionId ? { ...s, messages: next } : s));
      return next;
    });
    setMessage('');
    setLoading(true);
    try {
      const res = await fetch('http://localhost:8000/api/agent/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        body: JSON.stringify({ message, session_id: activeSessionId }),
      });

      if (!res.ok) throw new Error('Network response was not ok');
      const data = await res.json();
      const assistantMsg = { role: 'assistant', text: data.reply || 'No response' };
      setChat((c) => {
        const next = [...c, assistantMsg];
        if (activeSessionId) setSessions(prev => prev.map(s => s.id === activeSessionId ? { ...s, messages: next } : s));
        return next;
      });
    } catch (err) {
      setChat((c) => [...c, { role: 'assistant', text: 'Error: could not reach server.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    // Enter to send, Shift+Enter for newline
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!loading) sendMessage();
    }
  };

  // persist sessions and active session to localStorage (per-user keys)
  useEffect(() => {
    try {
      if (sessions && sessions.length) localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
      if (activeSessionId) localStorage.setItem(ACTIVE_KEY, activeSessionId);
    } catch (e) {
      // ignore storage errors
    }
  }, [sessions, activeSessionId, SESSIONS_KEY, ACTIVE_KEY]);

  // Scroll to latest message when session is opened or new messages arrive.
  // If the user has scrolled up (isAtBottomRef false), don't jump on new messages.
  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;

    const prevActive = prevActiveSessionRef.current;
    const sessionChanged = prevActive !== activeSessionId;
    prevActiveSessionRef.current = activeSessionId;

    if (sessionChanged) {
      // user switched/opened a session: always show latest
      el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
      setShowScrollToLatest(false);
      isAtBottomRef.current = true;
      return;
    }

    // For regular message updates, only auto-scroll if already near bottom
    if (isAtBottomRef.current) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      setShowScrollToLatest(false);
    }
  }, [chat, activeSessionId]);

  // One-time initial scroll: when the component first receives chat messages,
  // ensure we jump to the latest message so opening CommandX lands at the bottom.
  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    if (didInitialScrollRef.current) return;
    if (!chat || chat.length === 0) return;

    // Small timeout to ensure rendering/layout completed
    setTimeout(() => {
      try {
        el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
        didInitialScrollRef.current = true;
        isAtBottomRef.current = true;
        setShowScrollToLatest(false);
      } catch (e) {
        // ignore
      }
    }, 50);
  }, [chat]);

  return (
      <div className={`dashboard-container ${!isSidebarOpen ? 'sidebar-closed' : ''}`} style={{ minHeight: '100vh', position: 'relative' }}>
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        activeTab={activeTab}
        setActiveTab={(t) => { setActiveTab(t); if (t !== 'agent') navigate(`/${t}`); }}
        setIsLogoutConfirmOpen={setIsLogoutConfirmOpen} 
      />

      <main className="main-content" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100%', height: '100%', position: 'relative' }}>
        {showHello && (
          <div
            className="robot-hello-overlay"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              height: '100%',
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: document.documentElement.getAttribute('data-theme') === 'light' ? '#fff' : '#181c2f',
              transition: 'background 0.3s',
            }}
          >
            <div className="robot-hello-content">
              <span className="robot-emoji" role="img" aria-label="robot" style={{ fontSize: 64, display: 'inline-block', animation: 'waveHand 1.2s ease-in-out' }}>⚡</span>
              <div className="robot-hello-text">CommandX</div>
            </div>
          </div>
        )}
        <ProfileCard profileImage={profileImage} onClick={() => navigate('/profile')} />

        <div className="content-wrapper" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100%', height: '100%', opacity: showHello ? 0 : 1, transition: 'opacity 0.5s' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%', maxWidth: '1200px', flex: 1, minHeight: 0, justifyContent: 'center' }}>
              {/* Glassy card header like dashboard */}
              <div
                style={{
                  width: '100%',
                  padding: '18px',
                  borderRadius: '16px',
                  background: document.documentElement.getAttribute('data-theme') === 'light'
                    ? 'linear-gradient(135deg, #ffffff 0%, #f6f9ff 100%)'
                    : 'rgba(15, 15, 47, 0.6)',
                  border: document.documentElement.getAttribute('data-theme') === 'light'
                    ? '1.5px solid rgba(0, 224, 255, 0.2)'
                    : '1.5px solid rgba(0, 224, 255, 0.25)',
                  backdropFilter: 'blur(8px)',
                  color: document.documentElement.getAttribute('data-theme') === 'light' ? '#202124' : '#ffffff',
                  marginBottom: '12px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '24px' }}>⚡</span>
                    <div>
                      <h3
                        style={{
                          marginBottom: '4px',
                          fontSize: '20px',
                          fontWeight: '700',
                          background: document.documentElement.getAttribute('data-theme') === 'dark'
                            ? 'linear-gradient(90deg, #00fff7 10%, #fff 90%)'
                            : 'linear-gradient(135deg, #00e0ff, #9d50bb)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          margin: '0px',
                          textShadow: document.documentElement.getAttribute('data-theme') === 'dark'
                            ? '0 2px 24px #000000, 0 2px 24px #000000'
                            : '0 4px 16px rgba(100, 79, 79, 0.26)'
                        }}
                      >
                        CommandX
                      </h3>
                      <p style={{
                        color: document.documentElement.getAttribute('data-theme') === 'light'
                          ? 'rgba(0, 0, 0, 0.6)'
                          : 'rgba(255, 255, 255, 0.6)',
                        fontSize: '13px',
                        margin: '0px'
                      }}>Chat with CommandX to manage your tasks</p>
                    </div>
                  </div>
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, position: 'relative' }}>
                    <button
                      onClick={() => {
                        const id = `s_${Date.now()}`;
                        const title = `Chat ${sessions.length + 1}`;
                        const newSession = { id, title, messages: [] };
                        const next = [newSession, ...sessions];
                        setSessions(next);
                        setActiveSessionId(id);
                        setChat([]);
                        try { localStorage.setItem(SESSIONS_KEY, JSON.stringify(next)); localStorage.setItem(ACTIVE_KEY, id); } catch (e) {}
                        try { if (textareaRef && textareaRef.current) textareaRef.current.focus(); } catch (e) {}
                      }}
                      className="save-btn new-chat-btn"
                      style={{
                        height: '48px',
                        width: '155px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '14px 18px',
                        fontWeight: '600',
                        fontSize: '15px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'background 0.18s, box-shadow 0.18s',
                        background: 'linear-gradient(135deg, rgba(6,182,112,0.12), rgba(0,224,255,0.12))',
                      }}
                      title="Start a new chat"
                    >
                      <span style={{ fontSize: 18 }}>➕</span>
                      New Chat
                    </button>

                    <button
                      onClick={() => setIsDeleteChatConfirmOpen(true)}
                      className="save-btn delete-chat-btn"
                      style={{
                        height: '48px',
                        width: '155px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '14px 24px',
                        fontWeight: '600',
                        fontSize: '15px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'background 0.18s, box-shadow 0.18s',
                      }}
                      title="Delete all chat history"
                    >
                      <span style={{ fontSize: 20, display: 'inline-block' }}></span>
                      Delete Chat
                    </button>

                    <div style={{ width: 48, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 15 }}>
                      <button ref={sessionsToggleRef} onClick={() => setShowSessionsPanel(s => !s)} title="Open chats" style={{ width: 44, height: 44, borderRadius: 10, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="25" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 6h18M3 12h18M3 18h18" stroke="#00e0ff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                    </div>

                    {showSessionsPanel && (
                      <div ref={sessionsPanelRef} className="chat-sessions-panel" style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', zIndex: 3000, width: 360, maxHeight: '70vh', overflowY: 'auto', padding: 16, borderRadius: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                          <strong style={{ fontSize: 16 }}>Chat  History</strong>
                        </div>
                        {sessions.map(s => (
                          <div key={s.id} className={"chat-session" + (s.id === activeSessionId ? ' active' : '')} onClick={() => { setActiveSessionId(s.id); setChat(s.messages || []); setShowSessionsPanel(false); try { if (textareaRef && textareaRef.current) textareaRef.current.focus(); } catch (e) {} }} style={{ padding: '8px 10px', borderRadius: 8, marginBottom: 8, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 12, opacity: 0.8 }}>{(s.messages && s.messages.length) ? s.messages[s.messages.length-1].text.slice(0,60) : 'No messages yet'}</div>
                            </div>
                            <button
                              onClick={(ev) => {
                                ev.stopPropagation();
                                // remove session
                                setSessions(prev => {
                                  const next = prev.filter(x => x.id !== s.id);
                                  // if deleting active, choose next available
                                  if (s.id === activeSessionId) {
                                    const newActive = next[0] ? next[0].id : null;
                                    setActiveSessionId(newActive);
                                    setChat(newActive ? (next[0].messages || []) : []);
                                  }
                                  return next;
                                });
                                // also update localStorage immediately (per-user keys)
                                try {
                                  const stored = JSON.parse(localStorage.getItem(SESSIONS_KEY) || '[]');
                                  const updated = stored.filter(x => x.id !== s.id);
                                  localStorage.setItem(SESSIONS_KEY, JSON.stringify(updated));
                                  if (s.id === localStorage.getItem(ACTIVE_KEY)) {
                                    const newActive = updated[0] ? updated[0].id : null;
                                    if (newActive) localStorage.setItem(ACTIVE_KEY, newActive); else localStorage.removeItem(ACTIVE_KEY);
                                  }
                                } catch (e) {}
                              }}
                              className="session-delete-btn"
                              title="Delete session"
                              style={{ marginLeft: 10, border: 'none', background: 'transparent', color: '#ff7b7b', cursor: 'pointer', fontSize: 16 }}
                            >
                              🗑️
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
              </div>

                {/* Glassy chat card */}
              <div className="agent-box" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 320, position: 'relative' }}>
                {/* Chat area with scroll, takes all space above input */}
                <div
                  className="agent-messages"
                  ref={messagesRef}
                  onScroll={() => {
                    const el = messagesRef.current;
                    if (!el) return;
                    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
                    isAtBottomRef.current = nearBottom;
                    // show button only when user has scrolled away from bottom
                    setShowScrollToLatest(!nearBottom);
                  }}
                  style={{ flex: 1, overflowY: 'auto', marginBottom: 0, paddingBottom: 16 }}
                >
                  {chat.length === 0 && <div style={{ color: 'var(--muted-text, #9aa6ad)' }}>No messages yet — ask the CommandX to create or update tasks.</div>}
                  {chat.map((m, idx) => (
                    <div key={idx} className={`msg ${m.role === 'assistant' ? 'assistant' : 'user'}`} style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>
                        {m.role === 'assistant' ? <><span role="img" aria-label="agent">💬</span>CommandX</> : m.role === 'user' ? <><span role="img" aria-label="user">👤</span> You</> : m.role}
                      </div>
                      <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{m.text}</div>
                    </div>
                  ))}
                  {loading && (
                    <div className="msg assistant" style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 700 }}><span role="img" aria-label="agent">💬</span>CommandX</div>
                      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', height: 20 }}>
                        <span className="typing">
                          <span className="dot" />
                          <span className="dot" />
                          <span className="dot" />
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {showScrollToLatest && (
                  <button
                    onClick={() => {
                      const el = messagesRef.current;
                      if (!el) return;
                      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
                      setShowScrollToLatest(false);
                    }}
                    title="Go to latest"
                    style={{
                      position: 'absolute',
                      right: 24,
                      left: '92%',
                      bottom: 86,
                      zIndex: 2500,
                      background: 'linear-gradient(135deg, #ffffff, #ffffff)',
                      color: '#000000',
                      border: 'black solid 1px',
                      width: 35,
                      height: 34,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                      borderRadius: '50%',
                      fontSize: 25,
                      fontWeight: '900',
                      cursor: 'pointer'
                    }}
                  >
                    <span style={{ lineHeight: 1 }}>{'^'}</span>
                  </button>
                )}

                {/* Input bar always at bottom, not fixed */}
                <div className="agent-input-bar" style={{ marginTop: 12 }}>
                  <textarea
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    rows={3}
                    className="agent-textarea"
                    placeholder="Ask to create, update, complete, or list tasks"
                    style={{ resize: 'none', minHeight: 44, maxHeight: 120 }}
                  />

                  <button
                    onClick={sendMessage}
                    disabled={loading}
                    className="agent-send-btn gpt-send-btn"
                    title="Send"
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'linear-gradient(135deg, rgba(0,224,255,0.18), rgba(6,182,112,0.12))',
                      border: 'none',
                      boxShadow: '0 2px 8px rgba(0,224,255,0.10)',
                      transition: 'background 0.18s, box-shadow 0.18s',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      padding: 0
                    }}
                  >
                    {loading ? (
                      <div style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.25)', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ display: 'block' }}
                      >
                        <path
                          d="M4 20L20 12L4 4V10L16 12L4 14V20Z"
                          stroke="currentColor"
                          strokeWidth="1.7"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          fill="none"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
            </div>
          </div>
        </main>
      {/* Logout Modal */}
      <Modals
        isDeleteChatConfirmOpen={isDeleteChatConfirmOpen}
        setIsDeleteChatConfirmOpen={setIsDeleteChatConfirmOpen}
        isDeletingChat={isDeletingChat}
        handleDeleteChat={async () => {
          setIsDeletingChat(true);
          try {
            await API.delete('/api/agent/history', { headers: { Authorization: `Bearer ${token}` } });
            // clear current session messages and in-memory chat view
            setChat([]);
            if (activeSessionId) setSessions(prev => prev.map(s => s.id === activeSessionId ? { ...s, messages: [] } : s));
            try { const stored = JSON.parse(localStorage.getItem(SESSIONS_KEY) || '[]'); const updated = stored.map(s => s.id === activeSessionId ? { ...s, messages: [] } : s); localStorage.setItem(SESSIONS_KEY, JSON.stringify(updated)); } catch (e) {}
          } catch (err) {
            alert('Failed to delete chat history.');
          }
          setIsDeletingChat(false);
          setIsDeleteChatConfirmOpen(false);
        }}
        isLogoutConfirmOpen={isLogoutConfirmOpen}
        setIsLogoutConfirmOpen={setIsLogoutConfirmOpen}
        handleLogout={() => {
          try {
            // remove auth tokens and user info
            localStorage.removeItem("token");
            localStorage.removeItem("user");

            // Remove any agent session keys (legacy, token-derived, or per-user)
            try {
              Object.keys(localStorage).forEach((k) => {
                if (typeof k === 'string' && (k.startsWith('agent_sessions') || k.startsWith('agent_active_session'))) {
                  try { localStorage.removeItem(k); } catch (e) { /* ignore */ }
                }
              });
            } catch (e) { /* ignore enumeration errors */ }
          } catch (e) {
            // ignore any localStorage errors
          }
          navigate("/login", { replace: true });
        }}
      />
    </div>
  );
};

export default Agent;
